(() => {
  'use strict';

  const imageCache = new Map();
  const fontCache = new Set();
  let renderSeq = 0;
  let scheduled = false;

  // [v20 - fluidita'] stato per non ridisegnare quando non cambia niente
  let configCacheText = null;
  let configCacheValue = null;
  let configVersion = 0;
  let lastRenderedSig = null;
  let pendingSig = null;
  // [v20 - caricamento] l'anteprima parte quando serve (vicino allo schermo,
  // primo tocco del cliente o pagina gia' caricata), non insieme alla foto principale
  let renderEnabled = false;

  const GLOBO_ROOT_SELECTORS = [
    '.gpo-app',
    '.gpo-container',
    '.gpo-form',
    '.globo-formbuilder',
    '[id*="globo" i]',
    '[class*="globo" i]',
    '[id*="gpo" i]',
    '[class*="gpo" i]'
  ];

  function $(selector, root = document) { return root.querySelector(selector); }
  function $all(selector, root = document) { try { return Array.from(root.querySelectorAll(selector)); } catch (_) { return []; } }

  function clean(value) { return String(value ?? '').replace(/\s+/g, ' ').trim(); }
  function cleanPreviewText(value) {
    return String(value ?? '')
      .replace(/\r\n?/g, '\n')
      .split('\n')
      .map((line) => line.replace(/[ \t\f\v]+/g, ' ').trim())
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }
  function slugify(value) {
    return clean(value)
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }
  function norm(value) {
    return clean(value)
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }
  function escAttr(value) { return String(value || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"'); }

  function getRoot() { return $('[data-hb-preview]') || $('#hbPreviewRoot'); }
  function getCanvas(root = getRoot()) { return root?.querySelector('[data-hb-preview-canvas]') || $('#hbPreviewCanvas'); }
  function getConfigEl(root = getRoot()) { return root?.querySelector('[data-hb-preview-config]') || $('#hbPreviewConfig'); }

  function readConfig() {
    const el = getConfigEl();
    if (!el) return null;
    const text = el.textContent || 'null';
    // [v20] il config e' sempre lo stesso: lo leggiamo una volta sola
    if (text === configCacheText) return configCacheValue;
    try {
      let parsed = JSON.parse(text);
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      configCacheText = text;
      configCacheValue = parsed;
      configVersion += 1;
      return parsed;
    } catch (error) {
      console.error('[HB Preview] Config JSON non valido', error);
      return null;
    }
  }

  function showNotice(message) {
    const root = getRoot();
    const el = root?.querySelector('[data-hb-preview-notice]');
    const next = message || '';
    if (el && el.textContent !== next) el.textContent = next;
  }

  function applyUi(config) {
    const root = getRoot();
    if (!root || !config) return;
    const title = config.ui?.title || config.labels?.title || config.title || 'Crea la sua personalizzazione';
    const subtitle = config.ui?.subtitle || config.labels?.subtitle || config.subtitle || "Scegli le opzioni: l'anteprima si aggiorna in tempo reale.";
    const titleEl = root.querySelector('[data-hb-preview-title]');
    const subtitleEl = root.querySelector('[data-hb-preview-subtitle]');
    // [v20] scrive solo se cambia: ogni scrittura faceva ripartire il disegno
    if (titleEl && titleEl.textContent !== title) titleEl.textContent = title;
    if (subtitleEl && subtitleEl.textContent !== subtitle) subtitleEl.textContent = subtitle;
  }

  // ------------------------- posizione sopra Globo -------------------------
  function isVisible(el) {
    if (!el || !el.getBoundingClientRect) return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function findGloboRoot(root) {
    // Prima proviamo il contenitore intero Globo, non il singolo input. Così
    // finiamo sopra titolo + opzioni, non in mezzo al primo campo.
    for (const selector of GLOBO_ROOT_SELECTORS) {
      for (const el of $all(selector)) {
        if (!el || el === root || root.contains(el) || el.contains(root)) continue;
        const hasControls = !!el.querySelector('input, select, textarea, button, [role="button"], [aria-pressed], [aria-checked]');
        const text = norm(el.textContent || '');
        if (hasControls || text.includes('scegli') || text.includes('personalizz')) return el;
      }
    }

    // Se Globo non ha ancora il wrapper, cerchiamo dai selettori dei campi.
    const config = readConfig();
    const selectors = collectCandidateSelectors(config);
    for (const selector of selectors) {
      for (const node of $all(stripPseudo(selector))) {
        if (!node || root.contains(node)) continue;
        const gpo = node.closest('.gpo-app, .gpo-container, .gpo-element, .globo-formbuilder, fieldset, .product-form__input');
        if (gpo && !root.contains(gpo)) {
          return gpo.closest('.gpo-app, .gpo-container, .globo-formbuilder') || gpo;
        }
      }
    }
    return null;
  }

  function forceBeforeGlobo() {
    const root = getRoot();
    if (!root || root.dataset.hbForceBeforeGlobo !== 'true') return;
    // [v20] controllo veloce: se l'anteprima e' gia' subito sopra il form Globo
    // non serve cercarlo in tutta la pagina
    const next = root.nextElementSibling;
    if (next && next.matches && next.matches('.gpo-app')) return;
    const globo = findGloboRoot(root);
    if (!globo || !globo.parentNode) return;
    if (globo.previousElementSibling === root) return;
    try { globo.parentNode.insertBefore(root, globo); } catch (_) {}
  }

  function watchPosition() {
    const root = getRoot();
    if (!root || root.dataset.hbPositionWatch === 'true') return;
    root.dataset.hbPositionWatch = 'true';
    [0, 150, 400, 900, 1800, 3000].forEach((ms) => setTimeout(forceBeforeGlobo, ms));
    let pending = false;
    const obs = new MutationObserver(() => {
      if (pending) return;
      pending = true;
      setTimeout(() => { pending = false; forceBeforeGlobo(); }, 40);
    });
    try { obs.observe(document.body, { childList: true, subtree: true }); } catch (_) {}
  }

  // ------------------------- lettura campi Globo -------------------------
  function stripPseudo(selector) { return String(selector || '').replace(/:checked/g, '').replace(/:selected/g, '').trim(); }

  function fieldTerms(key, field = {}) {
    const terms = [
      key,
      field.key,
      field.name,
      field.sourceName,
      field.readableName,
      field.label,
      field.displayLabel
    ].filter(Boolean).map((v) => clean(v).replace(/^properties\[/i, '').replace(/\]$/g, ''));
    const out = [];
    terms.forEach((t) => {
      if (!t) return;
      [t, t.replace(/[_-]+/g, ' ')].forEach((v) => { if (v && !out.includes(v)) out.push(v); });
    });
    return out;
  }

  function selectorsForTerm(term, type = 'any') {
    const t = escAttr(term);
    const p = `properties[${t}]`;
    const choice = type === 'choice';
    const input = choice ? 'input:checked' : 'input';
    const textSelectors = choice ? [] : [
      `textarea[name="${t}"]`, `textarea[name="${p}"]`,
      `textarea[data-field-name="${t}"]`, `textarea[data-name="${t}"]`, `textarea[data-name="${p}"]`
    ];
    return [
      ...textSelectors,
      `${input}[name="${t}"]`, `${input}[name="${p}"]`,
      `${input}[data-field-name="${t}"]`, `${input}[data-name="${t}"]`, `${input}[data-name="${p}"]`,
      `select[name="${t}"]`, `select[name="${p}"]`, `select[data-field-name="${t}"]`, `select[data-name="${t}"]`, `select[data-name="${p}"]`,
      `[data-field-name="${t}"][aria-pressed="true"]`, `[data-field-name="${t}"][aria-checked="true"]`, `[data-field-name="${t}"].selected`, `[data-field-name="${t}"].active`, `[data-field-name="${t}"].is-selected`,
      `[data-name="${t}"][aria-pressed="true"]`, `[data-name="${p}"][aria-pressed="true"]`, `[aria-label="${t}"][aria-pressed="true"]`, `[aria-label="${t}"].selected`,
      `[data-value="${t}"].selected`, `[data-option-value="${t}"].selected`
    ];
  }

  function collectCandidateSelectors(config) {
    const out = [];
    Object.entries(config?.fields || {}).forEach(([key, field]) => {
      (field.selectors || []).forEach((selector) => {
        if (selector && !out.includes(selector)) out.push(selector);
        const stripped = stripPseudo(selector);
        if (stripped && !out.includes(stripped)) out.push(stripped);
      });
      fieldTerms(key, field).forEach((term) => selectorsForTerm(term, field.type).forEach((selector) => {
        if (!out.includes(selector)) out.push(selector);
      }));
    });
    return out;
  }

  function selectedLike(el) {
    if (!el) return false;
    const tag = (el.tagName || '').toLowerCase();
    if (tag === 'input') {
      const type = (el.type || '').toLowerCase();
      return type === 'radio' || type === 'checkbox' ? !!el.checked : true;
    }
    if (tag === 'option') return !!el.selected;
    if (tag === 'select' || tag === 'textarea') return true;
    const ap = String(el.getAttribute('aria-pressed') || '').toLowerCase();
    const ac = String(el.getAttribute('aria-checked') || '').toLowerCase();
    if (ap === 'true' || ac === 'true') return true;
    const cls = ` ${el.className || ''} `.toLowerCase();
    return cls.includes(' selected ') || cls.includes(' active ') || cls.includes(' checked ') || cls.includes(' is-selected ');
  }

  function valueFromElement(el, preserveNewlines = false) {
    if (!el) return '';
    const tag = (el.tagName || '').toLowerCase();
    const textClean = (value) => preserveNewlines ? cleanPreviewText(value) : clean(value);
    if (tag === 'select') {
      const opt = el.options?.[el.selectedIndex];
      // Ignora l'opzione segnaposto disabilitata (es. "-- Seleziona la frase --"):
      // non e' una scelta reale e non deve finire nell'anteprima.
      if (!opt || opt.disabled || (!clean(opt.value) && /selezion|--|scegli/i.test(opt.textContent || ''))) return '';
      return clean(opt.textContent || opt.value || el.value);
    }
    if (tag === 'textarea') return cleanPreviewText(el.value);
    if (tag === 'option') return el.selected ? clean(el.textContent || el.value) : '';
    if (tag === 'input') {
      const type = (el.type || '').toLowerCase();
      if ((type === 'radio' || type === 'checkbox') && !el.checked) return '';
      const raw = el.getAttribute('original-value') || el.getAttribute('data-value') || el.getAttribute('data-option-value') || el.value || el.getAttribute('value');
      return textClean(raw);
    }
    if (!selectedLike(el)) return '';
    return textClean(el.getAttribute('data-value') || el.getAttribute('data-option-value') || el.getAttribute('value') || el.getAttribute('aria-label') || el.textContent);
  }

  function valueInside(container, field = {}) {
    if (!container) return '';
    const preserveNewlines = field.type !== 'choice';
    const selectors = [
      'input[type="radio"]:checked', 'input[type="checkbox"]:checked',
      'textarea', 'select',
      '[aria-pressed="true"]', '[aria-checked="true"]', '.selected', '.active', '.checked', '.is-selected',
      'input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"])', 'input[type="hidden"]'
    ];
    for (const selector of selectors) {
      for (const el of $all(selector, container)) {
        const v = valueFromElement(el, preserveNewlines);
        if (v) return v;
      }
    }
    return valueFromElement(container, preserveNewlines);
  }

  function relatedTextareaValue(el) {
    if (!el) return '';
    const container = el.closest('.gpo-element, .gpo-form__group, .gpo-option, .gpo-item, fieldset, [data-element-id], [data-gpo], .product-form__input') || el.parentElement;
    const textarea = container?.querySelector?.('textarea');
    return textarea ? cleanPreviewText(textarea.value) : '';
  }

  function readBySelector(selector, field = {}) {
    const preserveNewlines = field.type !== 'choice';
    for (const el of $all(selector)) {
      // Globo sometimes mirrors a textarea into a hidden input with the same/related field.
      // Prefer the real textarea so customer Enter keys stay visible in the preview.
      const textareaValue = preserveNewlines && (el.tagName || '').toLowerCase() === 'input' ? relatedTextareaValue(el) : '';
      const v = textareaValue || valueFromElement(el, preserveNewlines) || valueInside(el, field);
      if (v) return v;
    }
    return '';
  }

  // True se esiste nel DOM un controllo identificato dal NOME esatto del campo
  // (name / data-name / data-field-name, con o senza properties[]). Serve a
  // distinguere "campo vuoto" da "campo assente": se il controllo c'e' ma e'
  // vuoto, il valore e' vuoto, e non va cercato altrove per etichetta.
  function hasNamedControl(key, field) {
    for (const term of fieldTerms(key, field)) {
      const t = escAttr(term);
      const p = `properties[${t}]`;
      const sels = [
        `[name="${t}"]`, `[name="${p}"]`,
        `[data-name="${t}"]`, `[data-name="${p}"]`,
        `[data-field-name="${t}"]`
      ];
      for (const s of sels) { try { if ($(s)) return true; } catch (_) {} }
    }
    return false;
  }

  function findContainerByLabel(key, field) {
    const terms = fieldTerms(key, field).map(norm).filter(Boolean);
    if (!terms.length) return null;
    const containers = $all('.gpo-element, .gpo-form__group, .gpo-option, .gpo-item, fieldset, [data-element-id], [data-gpo], .product-form__input');
    for (const c of containers) {
      const label = norm(c.getAttribute('data-label') || c.getAttribute('aria-label') || c.querySelector('.label-content, .gpo-label, legend, label')?.textContent || c.textContent);
      if (!label) continue;
      if (terms.some((t) => label === t || label.includes(t) || t.includes(label))) return c;
    }
    return null;
  }

  function mapValue(raw, field = {}) {
    const value = clean(raw);
    if (!value) return field.default || '';
    if (!field.map) return value;
    if (Object.prototype.hasOwnProperty.call(field.map, value)) return field.map[value];
    const s = slugify(value);
    if (Object.prototype.hasOwnProperty.call(field.map, s)) return field.map[s];
    const n = norm(value);
    for (const [k, v] of Object.entries(field.map)) {
      if ([k, v].some((x) => clean(x) === value || slugify(x) === s || norm(x) === n)) return v;
    }
    return field.default || value;
  }

  // [v21] La lista dei selettori di un campo non cambia finche' non cambia il
  // config: la costruiamo una volta sola invece che a ogni lettura (prima ogni
  // lettura ricreava ~200 stringhe e le confrontava tutte tra loro).
  let selectorCacheVersion = -1;
  const selectorCache = new Map();
  function fieldSelectorList(key, field) {
    if (selectorCacheVersion !== configVersion) { selectorCache.clear(); selectorCacheVersion = configVersion; }
    const cached = selectorCache.get(key);
    if (cached && cached.field === field) return cached.list;
    const seen = new Set();
    const selectors = [];
    const add = (selector) => { if (selector && !seen.has(selector)) { seen.add(selector); selectors.push(selector); } };

    // For text fields on Shopify/Globo, a textarea can be mirrored by hidden inputs.
    // Try real textarea controls first, otherwise manual customer line breaks can be lost.
    if (field.type !== 'choice') {
      fieldTerms(key, field).forEach((term) => {
        const t = escAttr(term);
        const p = `properties[${t}]`;
        add(`textarea[name="${t}"]`); add(`textarea[name="${p}"]`);
        add(`textarea[data-field-name="${t}"]`); add(`textarea[data-name="${t}"]`); add(`textarea[data-name="${p}"]`);
      });
    }

    (field.selectors || []).forEach((selector) => {
      add(selector);
      add(stripPseudo(selector));
    });
    fieldTerms(key, field).forEach((term) => selectorsForTerm(term, field.type).forEach(add));
    selectorCache.set(key, { field, list: selectors });
    return selectors;
  }

  // [v21] Durante un disegno lo stesso campo veniva letto fino a 3 volte
  // (valori, firma, testo del livello). Ora una volta sola per disegno.
  let passCache = null;

  function readField(key, field = {}) {
    if (passCache && passCache.has(key)) return passCache.get(key);
    const v = readFieldUncached(key, field);
    if (passCache) passCache.set(key, v);
    return v;
  }

  function readFieldUncached(key, field = {}) {
    const selectors = fieldSelectorList(key, field);

    for (const selector of selectors) {
      const v = readBySelector(selector, field);
      if (v) return v;
    }
    // Se nel DOM esiste un controllo con il NOME esatto di questo campo (anche
    // se vuoto o non selezionato), il campo e' semplicemente vuoto: NON ripiegare
    // sulla ricerca per etichetta. Quel ripiego, con match per sottostringa, puo'
    // pescare un campo vicino che condivide parole (es. "Frase" che becca
    // "Tipo di frase" o "Frase pronta") e restituire il valore sbagliato.
    if (hasNamedControl(key, field)) return field.default || '';
    const c = findContainerByLabel(key, field);
    const cv = valueInside(c, field);
    return cv || field.default || '';
  }

  function readValues(config) {
    const values = {};
    Object.entries(config.fields || {}).forEach(([key, field]) => {
      const raw = readField(key, field);
      if (field.type === 'choice') values[key] = mapValue(raw, field);
      else {
        let v = raw || field.default || '';
        const max = Number(field.maxLength || 0);
        if (max > 0) v = Array.from(String(v)).slice(0, max).join('');
        values[key] = v;
      }
    });
    return values;
  }

  // Testo effettivo di un layer: legge la prima sorgente compilata tra
  // textFrom e textFromFallback. Gestisce il caso "frase pronta o
  // personalizzata": Globo disabilita il campo non attivo, quindi solo una
  // sorgente restituisce testo. Per le tendine usa la frase scelta (testo
  // leggibile), non lo slug interno; scarta il default grezzo non scelto.
  function resolveLayerText(config, layer) {
    const sources = [layer && layer.textFrom, layer && layer.textFromFallback].filter(Boolean);
    for (const key of sources) {
      const field = (config.fields && config.fields[key]) || {};
      const v = readField(key, field);
      if (!v) continue;
      if (field.type === 'choice' && clean(v) === clean(field.default || '')) continue;
      if (clean(v)) return v;
    }
    return '';
  }


  // ------------------------- salvataggio a capo nel carrello/ordine -------------------------
  function isCartAddForm(form) {
    if (!form || (form.tagName || '').toLowerCase() !== 'form') return false;
    const action = String(form.getAttribute('action') || form.action || '').toLowerCase();
    return action.includes('/cart/add') || !!form.querySelector('input[name="id"], select[name="id"]');
  }

  function findProductForm(preferredForm) {
    if (isCartAddForm(preferredForm)) return preferredForm;
    const root = getRoot();
    const fromRoot = root?.closest?.('form');
    if (isCartAddForm(fromRoot)) return fromRoot;

    const productArea = root?.closest?.('product-info, .product, .product__info-container, .product-form, [id*="ProductInfo"], [class*="product" i]') || document;
    const localForms = $all('form[action*="/cart/add"], form[action*="cart/add"]', productArea).filter(isCartAddForm);
    if (localForms.length) return localForms.find(isVisible) || localForms[0];

    const allForms = $all('form[action*="/cart/add"], form[action*="cart/add"]', document).filter(isCartAddForm);
    return allForms.find(isVisible) || allForms[0] || null;
  }

  function lineItemBaseLabel(key, field = {}) {
    const raw = field.orderLabel || field.cartLabel || field.sourceName || field.name || field.readableName || field.label || field.displayLabel || key || 'Personalizzazione';
    return clean(raw)
      .replace(/^properties\[/i, '')
      .replace(/\]$/g, '')
      .replace(/^scrivi\s+la\s+/i, '')
      .replace(/^inserisci\s+la\s+/i, '')
      .replace(/\s+\*$/g, '')
      .trim() || 'Personalizzazione';
  }

  function orderLinesPropertyName(key, field = {}) {
    const explicit = clean(field.orderLinesProperty || field.linesProperty || field.cartLinesProperty || '');
    if (explicit) return explicit;
    const base = lineItemBaseLabel(key, field);
    return `${base} (righe separate)`;
  }

  function formatLinesForOrder(value) {
    const text = cleanPreviewText(value);
    if (!text || !text.includes('\n')) return '';
    const lines = text.split('\n');
    const hasRealSecondLine = lines.filter((line) => clean(line)).length > 1;
    if (!hasRealSecondLine) return '';
    return lines.map((line, index) => {
      const row = clean(line) || '(riga vuota)';
      return `Riga ${index + 1}: ${row}`;
    }).join(' | ');
  }

  function ensureLinePropertyInput(form, name) {
    if (!form || !name) return null;
    const selector = `input[type="hidden"][data-hb-lines-property="true"][name="properties[${escAttr(name)}]"]`;
    let input = form.querySelector(selector);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.dataset.hbLinesProperty = 'true';
      form.appendChild(input);
    }
    return input;
  }

  function syncOrderLineProperties(config = readConfig(), values = null, preferredForm = null) {
    if (!config?.fields) return;
    const form = findProductForm(preferredForm);
    if (!form) return;
    const currentValues = values || readValues(config);
    const activeNames = new Set();

    Object.entries(config.fields || {}).forEach(([key, field]) => {
      if (field.type === 'choice') return;
      const formatted = formatLinesForOrder(currentValues[key] || '');
      const name = orderLinesPropertyName(key, field);
      activeNames.add(name);
      const input = ensureLinePropertyInput(form, name);
      if (!input) return;
      // [v20] scrive solo se cambia
      if (input.value !== formatted) input.value = formatted;
      if (input.disabled !== !formatted) input.disabled = !formatted;
    });

    $all('input[type="hidden"][data-hb-lines-property="true"]', form).forEach((input) => {
      const match = String(input.name || '').match(/^properties\[(.*)\]$/);
      const propName = match ? match[1] : '';
      if (propName && !activeNames.has(propName)) input.remove();
    });
  }

  // ------------------------- assets/layer -------------------------
  function asArrayAssets(config) {
    const assets = config.assets;
    if (Array.isArray(assets)) return assets;
    const out = [];
    if (assets && typeof assets === 'object') {
      Object.entries(assets).forEach(([group, values]) => {
        if (values && typeof values === 'object') {
          Object.entries(values).forEach(([groupKey, src]) => out.push({ id: `${group}_${groupKey}`, group, groupKey, url: src }));
        }
      });
    }
    return out;
  }

  function srcFromAsset(asset) {
    if (!asset) return '';
    if (typeof asset === 'string') return asset;
    return asset.url || asset.src || asset.fileUrl || asset.imageUrl || asset.cdnUrl || '';
  }

  function resolveUrl(src, config) {
    if (!src) return '';
    const raw = String(src);
    if (/^https?:\/\//i.test(raw)) return raw;
    if (/^\/\//.test(raw)) return `${window.location.protocol}${raw}`;
    if (/^\//.test(raw)) return raw;
    const base = config.assetsBaseUrl || config.assetBaseUrl || config.assets_base_url || '';
    if (base) return base.replace(/\/$/, '/') + raw.replace(/^\//, '');
    // Vecchio sistema: file in Assets Shopify. asset_url non è disponibile in JS,
    // ma il CDN path corrente può essere dedotto dagli altri script solo con difficoltà.
    // Nel nuovo sistema dovrebbero essere URL assoluti da Shopify Files.
    return raw;
  }

  function optionVariants(config, fieldKey, value) {
    const set = new Set();
    const add = (v) => { if (v !== undefined && v !== null && String(v) !== '') { set.add(String(v)); set.add(slugify(v)); } };
    add(value);
    const field = config.fields?.[fieldKey];
    if (field?.map) {
      Object.entries(field.map).forEach(([raw, mapped]) => {
        if (String(mapped) === String(value) || slugify(raw) === slugify(value) || slugify(mapped) === slugify(value)) { add(raw); add(mapped); }
      });
    }
    (field?.options || []).forEach((o) => {
      if ([o.value, o.label, o.raw].some((x) => slugify(x) === slugify(value) || String(x || '') === String(value || ''))) {
        add(o.value); add(o.label); add(o.raw);
      }
    });
    return [...set];
  }

  function assetById(config, id) {
    return asArrayAssets(config).find((a) => a.id === id || slugify(a.id) === slugify(id));
  }

  function assetByGroup(config, group, variants) {
    const assets = asArrayAssets(config).filter((a) => a.group === group || slugify(a.group) === slugify(group));
    for (const v of variants) {
      const found = assets.find((a) => String(a.groupKey) === String(v) || slugify(a.groupKey) === slugify(v) || slugify(a.name) === slugify(v));
      if (found) return found;
    }
    return assets[0] || null;
  }

  function synthesizeLayers(config) {
    const layers = Array.isArray(config.layers) ? config.layers.slice() : [];
    const hasBase = layers.some((l) => l.role === 'base' || l.id === 'base');
    if (!hasBase) {
      if (config.base?.mode === 'fixed' && config.base.assetId) {
        layers.unshift({ id: 'base', role: 'base', type: 'image', imageSource: 'asset', assetId: config.base.assetId, fullCanvas: true, fit: 'cover' });
      } else if (config.base?.mode === 'field' && config.base.fieldKey) {
        layers.unshift({ id: 'base', role: 'base', type: 'image', imageSource: 'group', assetGroup: config.base.assetGroup || 'base', valueFrom: config.base.fieldKey, fullCanvas: true, fit: 'cover' });
      } else if (config.baseImage || config.baseImageAssetId) {
        layers.unshift({ id: 'base', role: 'base', type: 'image', source: 'baseImage', assetId: config.baseImageAssetId, fullCanvas: true, fit: 'cover' });
      }
    }
    return layers;
  }

  function sourceForImageLayer(config, layer, values) {
    if (layer.source === 'baseImage' || layer.imageSource === 'base') {
      return resolveUrl(config.baseImage || srcFromAsset(assetById(config, config.baseImageAssetId)), config);
    }
    if (layer.imageSource === 'asset' || layer.assetId) {
      return resolveUrl(srcFromAsset(assetById(config, layer.assetId)), config);
    }
    if (layer.imageSource === 'group' || layer.assetGroup) {
      const fieldValue = values[layer.valueFrom] || config.fields?.[layer.valueFrom]?.default || '';
      const variants = optionVariants(config, layer.valueFrom, fieldValue);
      return resolveUrl(srcFromAsset(assetByGroup(config, layer.assetGroup, variants)), config);
    }
    return '';
  }

  function loadImage(src) {
    const url = resolveUrl(src || '', readConfig() || {});
    if (!url) return Promise.resolve(null);
    if (imageCache.has(url)) return imageCache.get(url);
    const p = new Promise((resolve) => {
      const img = new Image();
      img.decoding = 'async';
      img.crossOrigin = 'anonymous';
      // [v20] decode() e' solo un'ottimizzazione: con la scheda in background puo' non
      // rispondere mai e lasciare l'anteprima vuota. Dopo 800 ms si disegna comunque.
      img.onload = async () => {
        try {
          if (img.decode) await Promise.race([img.decode(), new Promise((r) => setTimeout(r, 800))]);
        } catch (_) {}
        resolve(img);
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
    imageCache.set(url, p);
    return p;
  }

  // [v21] le immagini ricolorate (es. disegno nel colore della scritta) si
  // preparano una volta e si riusano, invece di rifarle a ogni disegno
  const tintCache = new WeakMap();
  function drawImageAt(ctx, img, x, y, w, h, tint = '') {
    if (!img) return;
    if (!tint) { ctx.drawImage(img, x, y, w, h); return; }
    const ow = Math.max(1, Math.round(w));
    const oh = Math.max(1, Math.round(h));
    const key = `${tint}|${ow}|${oh}`;
    let perImg = tintCache.get(img);
    if (!perImg) { perImg = new Map(); tintCache.set(img, perImg); }
    let off = perImg.get(key);
    if (!off) {
      off = document.createElement('canvas');
      off.width = ow;
      off.height = oh;
      const o = off.getContext('2d');
      o.drawImage(img, 0, 0, ow, oh);
      o.globalCompositeOperation = 'source-in';
      o.fillStyle = tint;
      o.fillRect(0, 0, ow, oh);
      perImg.set(key, off);
      if (perImg.size > 12) perImg.delete(perImg.keys().next().value);
    }
    ctx.drawImage(off, x, y, w, h);
  }

  function drawImageFit(ctx, img, area, fit = 'contain', tint = '') {
    if (!img || !area) return;
    const iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    if (!iw || !ih) return;
    const scale = fit === 'cover' ? Math.max(area.width / iw, area.height / ih) : Math.min(area.width / iw, area.height / ih);
    const w = iw * scale, h = ih * scale;
    const x = area.x + (area.width - w) / 2, y = area.y + (area.height - h) / 2;
    ctx.save();
    ctx.beginPath(); ctx.rect(area.x, area.y, area.width, area.height); ctx.clip();
    drawImageAt(ctx, img, x, y, w, h, tint);
    ctx.restore();
  }

  function resolveTint(layer, values) {
    if (!layer.tintMode || layer.tintMode === 'none') return '';
    if (layer.tintMode === 'fixed') return layer.tintColor || '';
    const v = values[layer.tintFrom] || '';
    return layer.tintMap?.[v] || layer.tintMap?.[slugify(v)] || layer.tintColor || '';
  }

  // ------------------------- fonts/testo -------------------------
  async function ensureFonts(config) {
    if (!document.fonts) return;
    const jobs = [];
    for (const font of config.fonts || []) {
      const key = font.key || font.family || font.label;
      if (!key || fontCache.has(key)) continue;
      const family = font.family || key;
      const url = font.fileUrl || font.url;
      if (url && window.FontFace) {
        const face = new FontFace(family, `url("${url}")`);
        jobs.push(face.load().then((loaded) => { document.fonts.add(loaded); fontCache.add(key); }).catch(() => fontCache.add(key)));
      } else if (document.fonts.load) {
        jobs.push(document.fonts.load(`${font.previewSize || 100}px ${font.cssStack || `"${family}", sans-serif`}`).then(() => fontCache.add(key)).catch(() => fontCache.add(key)));
      }
    }
    await Promise.all(jobs).catch(() => {});
  }

  function matchFont(config, key) {
    if (!config.fonts?.length) return {};
    const target = slugify(key);
    return config.fonts.find((f) => [f.key, f.label, f.family].some((x) => slugify(x) === target))
      || config.fonts.find((f) => [f.key, f.label, f.family].some((x) => slugify(x).includes(target) || target.includes(slugify(x))))
      || config.fonts[0];
  }

  function mapLookup(map, variants) {
    if (!map) return '';
    for (const v of variants) {
      if (Object.prototype.hasOwnProperty.call(map, v)) return map[v];
      const s = slugify(v);
      if (Object.prototype.hasOwnProperty.call(map, s)) return map[s];
    }
    return '';
  }

  function resolveFontKey(config, layer, values) {
    const fallback = layer.fixedFont || config.fonts?.[0]?.key || '';
    if (!layer.fontFrom) return fallback;
    const raw = values[layer.fontFrom] || config.fields?.[layer.fontFrom]?.default || '';
    const variants = optionVariants(config, layer.fontFrom, raw);
    return mapLookup(layer.fontMap, variants) || matchFont(config, raw).key || fallback;
  }

  function rulesForLayer(layer, fontKey) {
    const base = { ...(layer.textRules || {}) };
    const per = layer.fontRules?.[fontKey] || {};
    const merged = { ...base, ...per };
    if (!merged.caseMode) merged.caseMode = merged.uppercase ? 'uppercase' : (merged.capitalizeFirst ? 'capitalize' : 'none');
    return merged;
  }

  function transformText(text, rules) {
    const value = cleanPreviewText(text);
    if (rules.caseMode === 'uppercase') return value.toUpperCase();
    if (rules.caseMode === 'capitalize') return value.replace(/^(\s*)(\S)/, (m, s, c) => s + c.toUpperCase());
    return value;
  }

  function limitText(config, layer, text) {
    const explicit = Number(layer.textRules?.maxCharacters || 0);
    const fromField = Number(config.fields?.[layer.textFrom]?.maxLength || 0);
    const max = explicit > 0 ? explicit : (layer.textRules?.useGloboMaxLength === false ? 0 : fromField);
    return max > 0 ? Array.from(String(text || '')).slice(0, max).join('') : String(text || '');
  }

  function ruleNumber(value, fallback) {
    const normalized = String(value ?? '').replace(',', '.');
    const n = Number(normalized);
    return Number.isFinite(n) ? n : fallback;
  }

  function lineHeightValue(rules = {}) {
    // Allow tight line-height when needed. The previous 0.6 floor made low
    // values look unchanged, especially after the font had shrunk.
    return Math.max(0.1, ruleNumber(rules.lineHeight, 1.1));
  }

  function textBlockHeight(lineCount, size, lineHeight) {
    const count = Math.max(1, Number(lineCount) || 1);
    // Line-height is the distance between baselines. A single line should not
    // become taller just because interlinea is high; only gaps between lines do.
    return size + Math.max(0, count - 1) * size * lineHeight;
  }

  function textSignature(value) {
    return String(value || '').replace(/\s/g, '');
  }

  function linesShowAllText(lines, sourceText) {
    return textSignature(lines.join('')).length >= textSignature(sourceText).length;
  }

  function splitLongWord(ctx, word, size, stack, maxWidth) {
    ctx.font = `${size}px ${stack}`;
    const pieces = [];
    let cur = '';
    Array.from(String(word || '')).forEach((ch) => {
      const test = cur + ch;
      if (cur && ctx.measureText(test).width > maxWidth) { pieces.push(cur); cur = ch; }
      else cur = test;
    });
    if (cur) pieces.push(cur);
    return pieces.length ? pieces : [''];
  }

  function wrapParagraphAtSize(ctx, text, size, stack, maxWidth, maxLines, breakLongWords = false) {
    ctx.font = `${size}px ${stack}`;
    const lines = [];
    let cur = '';
    const words = String(text || '').split(' ').filter(Boolean);

    words.forEach((word) => {
      if (lines.length >= maxLines) return;
      const wordTooWide = ctx.measureText(word).width > maxWidth;
      const parts = wordTooWide && breakLongWords ? splitLongWord(ctx, word, size, stack, maxWidth) : [word];
      parts.forEach((part, idx) => {
        if (lines.length >= maxLines) return;
        const sep = cur && idx === 0 ? ' ' : '';
        const test = cur ? `${cur}${sep}${part}` : part;
        if (cur && ctx.measureText(test).width > maxWidth) { lines.push(cur); cur = part; }
        else cur = test;
      });
    });
    if (cur && lines.length < maxLines) lines.push(cur);
    return lines.length ? lines : [''];
  }

  function wrapAtSize(ctx, text, size, stack, maxWidth, maxLines, breakLongWords = false) {
    const lines = [];
    String(text || '').split('\n').forEach((paragraph) => {
      if (lines.length >= maxLines) return;
      if (!paragraph.trim()) { lines.push(''); return; }
      lines.push(...wrapParagraphAtSize(ctx, paragraph, size, stack, maxWidth, maxLines - lines.length, breakLongWords));
    });
    return lines.slice(0, maxLines);
  }

  function fits(ctx, lines, size, stack, area, lineHeight) {
    ctx.font = `${size}px ${stack}`;
    return lines.every((l) => ctx.measureText(l).width <= area.width + 0.5)
      && textBlockHeight(lines.length, size, lineHeight) <= area.height + 0.5;
  }

  function shrinkToFitLines(ctx, lines, stack, area, max, min, lineHeight) {
    let size = max;
    while (size > min && !fits(ctx, lines, size, stack, area, lineHeight)) size -= 1;
    return size;
  }

  function bestWrappedFit(ctx, text, stack, area, max, min, lineHeight, lineLimit) {
    let best = null;
    let lo = min;
    let hi = max;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      // Important: do NOT split normal words while searching. Reduce the font first.
      // Split a word into characters only as an emergency fallback, otherwise words like
      // "funzionante" can become "funzionant / e" even when a smaller 2-line fit exists.
      const lines = wrapAtSize(ctx, text, mid, stack, area.width, lineLimit, false);
      const shown = linesShowAllText(lines, text);
      if (shown && fits(ctx, lines, mid, stack, area, lineHeight)) {
        best = { lines, fontSize: mid };
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return best;
  }

  function areaRotationRad(area) {
    const deg = Number(area && area.rotation) || 0;
    return (deg * Math.PI) / 180;
  }

  // Esegue fn con il contesto ruotato attorno al centro dell'area. Dentro fn si
  // disegna come se l'area fosse orizzontale: la rotazione e' gia' applicata.
  function withAreaRotation(ctx, area, fn) {
    const rad = areaRotationRad(area);
    if (!rad) { fn(); return; }
    const cx = area.x + area.width / 2;
    const cy = area.y + area.height / 2;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rad);
    ctx.translate(-cx, -cy);
    fn();
    ctx.restore();
  }

  function letterSpacingPx(rules) {
    const n = ruleNumber(rules.letterSpacing, 0);
    return Number.isFinite(n) ? n : 0;
  }

  function applyLetterSpacing(ctx, px) {
    // Canvas moderno: letterSpacing influenza sia measureText sia fillText, quindi
    // il wrapping resta corretto. Sui browser senza supporto e' semplicemente ignorato.
    try { ctx.letterSpacing = `${px}px`; } catch (_) {}
  }

  function fitText(ctx, text, stack, rules, area) {
    const rawMax = Math.max(8, ruleNumber(rules.maxFontSize, 100));
    const rawMin = Math.max(4, ruleNumber(rules.minFontSize, 26));
    const max = Math.max(rawMax, rawMin);
    const min = Math.min(rawMin, max);
    const emergencyMin = 4;
    const maxLines = Math.max(1, Math.min(6, Math.round(ruleNumber(rules.maxLines, 2))));
    const lineHeight = lineHeightValue(rules);
    const forceAfterRaw = ruleNumber(rules.forceWrapAfterChars, 0);
    const forceAfter = forceAfterRaw > 0 ? forceAfterRaw : Infinity;
    const wrapBelowRaw = ruleNumber(rules.wrapBelowPx, 0);
    const wrapBelow = wrapBelowRaw > 0 ? Math.max(min, wrapBelowRaw) : 0;
    const mode = rules.mode || 'shrinkThenWrap';
    const cleanText = cleanPreviewText(text);
    const hasManualBreaks = cleanText.includes('\n');
    const count = Array.from(cleanText.replace(/\s/g, '')).length;

    if (!cleanText) return { lines: [''], fontSize: min };

    if (hasManualBreaks) {
      for (let limit = Math.min(maxLines, cleanText.split('\n').length); limit <= maxLines; limit += 1) {
        const best = bestWrappedFit(ctx, cleanText, stack, area, max, min, lineHeight, limit);
        if (best) return best;
      }
      for (let limit = Math.min(maxLines, cleanText.split('\n').length); limit <= maxLines; limit += 1) {
        const best = bestWrappedFit(ctx, cleanText, stack, area, min, emergencyMin, lineHeight, limit);
        if (best) return best;
      }
      return { lines: wrapAtSize(ctx, cleanText, emergencyMin, stack, area.width, maxLines, true), fontSize: emergencyMin };
    }

    const singleText = cleanText.replace(/\s+/g, ' ');
    const oneLine = [singleText];
    const single = shrinkToFitLines(ctx, oneLine, stack, area, max, min, lineHeight);
    const singleFits = fits(ctx, oneLine, single, stack, area, lineHeight);

    const explicitCharWrap = Number.isFinite(forceAfter) && count > forceAfter;
    const explicitPxWrap = wrapBelow > 0 && single <= wrapBelow;
    const shouldWrap = maxLines > 1 && mode !== 'shrink' && (mode === 'wrap' || explicitCharWrap || explicitPxWrap || !singleFits);
    if (!shouldWrap) {
      if (singleFits) return { lines: oneLine, fontSize: single };
      const emergencySingle = shrinkToFitLines(ctx, oneLine, stack, area, max, emergencyMin, lineHeight);
      if (fits(ctx, oneLine, emergencySingle, stack, area, lineHeight)) return { lines: oneLine, fontSize: emergencySingle };
      return { lines: wrapAtSize(ctx, singleText, emergencyMin, stack, area.width, maxLines, true), fontSize: emergencyMin };
    }

    const minAutoLines = 2;
    for (let lineLimit = Math.min(maxLines, minAutoLines); lineLimit <= maxLines; lineLimit += 1) {
      const best = bestWrappedFit(ctx, singleText, stack, area, max, min, lineHeight, lineLimit);
      if (best) return best;
    }
    for (let lineLimit = Math.min(maxLines, minAutoLines); lineLimit <= maxLines; lineLimit += 1) {
      const best = bestWrappedFit(ctx, singleText, stack, area, min, emergencyMin, lineHeight, lineLimit);
      if (best) return best;
    }
    return { lines: wrapAtSize(ctx, singleText, emergencyMin, stack, area.width, maxLines, true), fontSize: emergencyMin };
  }

  function matrixLookup(matrix, rowVal, colVal) {
    if (!matrix) return '';
    const rowKeys = [rowVal, slugify(rowVal)];
    const colKeys = [colVal, slugify(colVal)];
    for (const r of rowKeys) {
      const rowMap = matrix[r];
      if (rowMap) {
        for (const c of colKeys) {
          if (Object.prototype.hasOwnProperty.call(rowMap, c)) return rowMap[c];
        }
      }
    }
    return '';
  }

  function resolveTextColor(layer, values) {
    const mode = (layer.colorMode === 'matrix' || layer.colorMode === 'field' || layer.colorMode === 'fixed')
      ? layer.colorMode
      : (layer.colorFrom ? 'field' : 'fixed');
    if (mode === 'matrix' && layer.colorRowFrom && layer.colorColFrom) {
      const rowVal = values[layer.colorRowFrom] || '';
      const colVal = values[layer.colorColFrom] || '';
      return matrixLookup(layer.colorMatrix, rowVal, colVal) || layer.color || '#483023';
    }
    if (mode === 'field' && layer.colorFrom) {
      const v = values[layer.colorFrom] || '';
      return layer.colorMap?.[v] || layer.colorMap?.[slugify(v)] || layer.color || '#483023';
    }
    return layer.color || '#483023';
  }

  // ------------------------- render -------------------------
  function renderSignature(config, values, texts) {
    return JSON.stringify([configVersion, values, texts]);
  }

  async function render() {
    const config = readConfig();
    const root = getRoot();
    const canvas = getCanvas(root);
    if (!config || !root || !canvas) return;
    applyUi(config);

    // [v21] tutte le letture dei campi di questo disegno in un colpo solo
    // (sincrono: nessun altro codice puo' usare questi valori in memoria)
    passCache = new Map();
    let values, layerTexts;
    try {
      values = readValues(config);
      layerTexts = new Map();
      synthesizeLayers(config).forEach((layer) => {
        if (layer.type === 'text') layerTexts.set(layer, resolveLayerText(config, layer));
      });
    } finally {
      passCache = null;
    }
    syncOrderLineProperties(config, values);

    // [v20] stesse scelte dell'ultimo disegno (o di quello in corso): niente da ridisegnare.
    // Prima si ridisegnava di continuo, anche 25 volte al secondo a pagina ferma.
    const sig = renderSignature(config, values, [...layerTexts.values()]);
    if (sig === pendingSig || (sig === lastRenderedSig && root.classList.contains('is-ready'))) {
      forceBeforeGlobo();
      return;
    }
    const seq = ++renderSeq;
    pendingSig = sig;
    lastRenderedSig = null;

    const width = Number(config.canvas?.width || 1000);
    const height = Number(config.canvas?.height || width);
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    await ensureFonts(config);
    if (seq !== renderSeq) return;

    let drewSomething = false;
    const missing = [];

    for (const layer of synthesizeLayers(config)) {
      if (seq !== renderSeq) return;
      if (layer.type === 'image') {
        const src = sourceForImageLayer(config, layer, values);
        if (!src) { missing.push(layer.id || layer.name || 'immagine'); continue; }
        const img = await loadImage(src);
        if (seq !== renderSeq) return;
        if (!img) { missing.push(layer.id || src); continue; }
        const area = layer.fullCanvas || layer.role === 'base'
          ? { x: 0, y: 0, width, height }
          : config.areas?.[layer.area];
        if (!area) continue;
        const tint = resolveTint(layer, values);
        if (layer.fullCanvas || layer.role === 'base') drawImageAt(ctx, img, 0, 0, width, height, tint);
        else withAreaRotation(ctx, area, () => drawImageFit(ctx, img, area, layer.fit || 'contain', tint));
        drewSomething = true;
      }

      if (layer.type === 'text') {
        const area = config.areas?.[layer.area];
        if (!area) continue;
        const raw = limitText(config, layer, layerTexts.has(layer) ? layerTexts.get(layer) : resolveLayerText(config, layer));
        const fontKey = resolveFontKey(config, layer, values);
        const font = matchFont(config, fontKey);
        const rules = rulesForLayer(layer, font.key || fontKey);
        const text = transformText(raw, rules);
        if (!text) continue;
        const stack = font.cssStack || (font.family ? `"${font.family}", sans-serif` : 'Arial, sans-serif');
        const spacing = letterSpacingPx(rules);
        applyLetterSpacing(ctx, spacing);
        const fitted = fitText(ctx, text, stack, rules, area);
        const lh = fitted.fontSize * lineHeightValue(rules);
        const totalH = lh * (fitted.lines.length - 1);
        let y = area.y + area.height / 2 - totalH / 2;
        withAreaRotation(ctx, area, () => {
          ctx.save();
          ctx.beginPath();
          ctx.rect(area.x, area.y, area.width, area.height);
          ctx.clip();
          ctx.fillStyle = resolveTextColor(layer, values);
          ctx.textAlign = area.align || 'center';
          ctx.textBaseline = 'middle';
          ctx.font = `${fitted.fontSize}px ${stack}`;
          applyLetterSpacing(ctx, spacing);
          fitted.lines.forEach((line, i) => {
            let x = area.x + area.width / 2;
            if (area.align === 'left') x = area.x;
            if (area.align === 'right') x = area.x + area.width;
            ctx.fillText(line, x, y + i * lh);
          });
          ctx.restore();
        });
        applyLetterSpacing(ctx, 0);
        drewSomething = true;
      }
    }

    if (!drewSomething) {
      showNotice('Anteprima vuota: controlla che la base sia selezionata/pubblicata e che gli asset abbiano un URL.');
      console.warn('[HB Preview] Nessun layer disegnato', { config, values, missing });
    } else {
      showNotice('');
    }
    if (seq === renderSeq) {
      lastRenderedSig = sig;
      pendingSig = null;
    }
    if (!root.classList.contains('is-ready')) root.classList.add('is-ready');
    forceBeforeGlobo();
  }

  // [v20] se un disegno va in errore, il successivo deve poter ripartire
  function runRender() {
    return Promise.resolve()
      .then(render)
      .catch((error) => {
        pendingSig = null;
        lastRenderedSig = null;
        console.warn('[HB Preview] Errore render:', error);
      });
  }

  function scheduleRender() {
    if (!renderEnabled) return;
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; runRender(); });
  }

  function attachListeners() {
    if (document.documentElement.dataset.hbPreviewListeners === 'true') return;
    document.documentElement.dataset.hbPreviewListeners = 'true';
    // [v21] il disegno aggiorna gia' i campi nascosti dell'ordine: niente seconda
    // lettura completa di tutti i campi a ogni tasto premuto
    document.addEventListener('input', () => { renderEnabled = true; scheduleRender(); }, true);
    document.addEventListener('change', () => { renderEnabled = true; scheduleRender(); }, true);
    document.addEventListener('submit', (event) => syncOrderLineProperties(readConfig(), null, event.target), true);
    document.addEventListener('pointerdown', (event) => {
      if (event.target?.closest?.('button, input[type="submit"], [name="add"], [type="submit"]')) syncOrderLineProperties();
    }, true);
    document.addEventListener('click', (event) => {
      if (event.target?.closest?.('button, input[type="submit"], [name="add"], [type="submit"]')) syncOrderLineProperties();
      setTimeout(scheduleRender, 80);
    }, true);
    // [v20] ignora le modifiche fatte dall'anteprima stessa (titolo, classe is-ready,
    // campi nascosti dell'ordine) e quelle in altre sezioni della pagina (slider recensioni...):
    // erano loro a far ripartire il disegno all'infinito.
    const isOwnNode = (node, root) => !!node && (
      (root && (node === root || root.contains(node))) ||
      (node.dataset && node.dataset.hbLinesProperty === 'true')
    );
    const obs = new MutationObserver((mutations) => {
      const root = getRoot();
      const scope = root && root.closest ? root.closest('.shopify-section') : null;
      for (const m of mutations) {
        const t = m.target;
        if (isOwnNode(t, root)) continue;
        if (scope && !scope.contains(t)) continue;
        if (m.type === 'childList') {
          const nodes = [...m.addedNodes, ...m.removedNodes];
          if (nodes.length && nodes.every((n) => n.nodeType !== 1 || isOwnNode(n, root))) continue;
        }
        setTimeout(scheduleRender, 70);
        return;
      }
    });
    try {
      obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'value', 'checked', 'selected', 'aria-checked', 'aria-pressed'] });
    } catch (_) {}
  }

  function preload(config) {
    asArrayAssets(config).forEach((a) => { const src = resolveUrl(srcFromAsset(a), config); if (src) loadImage(src); });
  }

  function init() {
    const root = getRoot();
    const config = readConfig();
    if (!root || !config) return;
    root.dataset.hbForceBeforeGlobo = root.dataset.hbForceBeforeGlobo || String(config.ui?.forceBeforeGlobo !== false);
    applyUi(config);
    attachListeners();
    watchPosition();
    startWhenNeeded(root, config);
  }

  // [v20] Immagini e font dell'anteprima partono quando servono davvero:
  // - l'anteprima si avvicina allo schermo, oppure
  // - il cliente tocca un campo, oppure
  // - la pagina ha finito di caricare (foto principale gia' mostrata).
  // Le immagini delle altre scelte (altri colori, altri fiocchi) si scaricano dopo,
  // nei momenti liberi del browser.
  let started = false;
  function startWhenNeeded(root, config) {
    let io = null;
    const go = () => {
      if (io) { io.disconnect(); io = null; }
      if (started) return;
      started = true;
      renderEnabled = true;
      runRender().finally(() => {
        const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 600));
        idle(() => preload(readConfig() || config), { timeout: 4000 });
      });
    };
    if (started) { renderEnabled = true; runRender(); return; }
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) go();
      }, { rootMargin: '300px 0px' });
      io.observe(root);
    } else {
      go();
      return;
    }
    const onFirstTouch = () => go();
    document.addEventListener('input', onFirstTouch, { once: true, capture: true });
    document.addEventListener('change', onFirstTouch, { once: true, capture: true });
    document.addEventListener('pointerdown', onFirstTouch, { once: true, capture: true, passive: true });
    const afterLoad = () => {
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
      idle(go, { timeout: 2500 });
    };
    if (document.readyState === 'complete') afterLoad();
    else window.addEventListener('load', afterLoad, { once: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();